import { Box, FormControl, FormLabel, Stack } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

interface DateRangePickerProps {
  label: string;
  value: [string, string];
  onChange: (newRange: [string, string]) => void;
  minDateStart?: Dayjs;
  minDateEnd?: Dayjs;
  maxDate?: Dayjs;
  error?: string;
  isEducation?: boolean;
  clampToBounds?: boolean;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  value,
  onChange,
  minDateStart,
  minDateEnd,
  maxDate,
  error,
  isEducation,
  clampToBounds = false,
}) => {
  const unit = isEducation ? "year" : "month";

  const toDayjs = (s: string) => {
    if (!s) return null;
    return isEducation ? dayjs(`${s}-01-01`) : dayjs(s);
  };

  const serialize = (d: Dayjs | null) => {
    if (!d || !d.isValid()) return "";
    return isEducation ? String(d.year()) : d.toISOString();
  };

  // Ensures manually typed dates stay valid when clampToBounds=true:
  //  - Snap to minDateStart if user types a date before the allowed range
  //  - Snap to maxDate if user types a date after the allowed range

  // This is mainly used in projectPeriod pickers, where a project
  // must stay within the affiliation’s date range. Other contexts
  // can leave clampToBounds=false so typing is unrestricted.

  const clamp = (d: Dayjs | null, idx: 0 | 1): Dayjs | null => {
    if (!clampToBounds || !d || !d.isValid()) return d;

    // hard bounds
    if (minDateStart && d.isBefore(minDateStart, unit)) d = minDateStart;
    if (maxDate && d.isAfter(maxDate, unit)) d = maxDate;

    // keep order (To ≥ From, From ≤ To)
    const other = toDayjs(value[idx === 0 ? 1 : 0]);
    if (idx === 1 && other && d.isBefore(other, unit)) d = other;
    if (idx === 0 && other && d.isAfter(other, unit)) d = other;

    return d;
  };

  const handleChange = (index: 0 | 1, date: Dayjs | null) => {
    const newRange: [string, string] = [...value] as [string, string];
    const normalized = clamp(date, index);
    newRange[index] = serialize(normalized);
    onChange(newRange);
  };

  return (
    <FormControl fullWidth>
      <FormLabel>{label}</FormLabel>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack direction="row" spacing={2}>
          <Box flex={1}>
            <DatePicker
              views={isEducation ? ["year"] : ["year", "month"]}
              label="From"
              value={toDayjs(value[0])}
              minDate={minDateStart}
              maxDate={maxDate}
              disableFuture
              onChange={(date) => handleChange(0, date)}
              slotProps={{
                textField: {
                  error: !!error,
                  helperText: error ?? null,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      padding: "9px 15px",
                      height: "50px",
                    },
                  },
                },
              }}
            />
          </Box>
          <Box flex={1}>
            <DatePicker
              views={isEducation ? ["year"] : ["year", "month"]}
              label="To"
              value={toDayjs(value[1])}
              minDate={minDateEnd}
              maxDate={maxDate}
              disableFuture
              onChange={(date) => handleChange(1, date)}
              slotProps={{
                textField: {
                  error: !!error,
                  helperText: error ?? null,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      padding: "9px 15px",
                      height: "50px",
                    },
                  },
                },
              }}
            />
          </Box>
        </Stack>
      </LocalizationProvider>
    </FormControl>
  );
};
